import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { DashboardWorkerOnShiftResponse } from '@core/generated/gql-types';
import { actionLinkClassName } from '@core/theme/actionTheme.util';
import { useClientStore } from '@core/client/useClientStore';
import { useJobStore } from '@core/job/useJobStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import JobDrawer from '@/modules/jobs/components/JobDrawer';
import useFeatureView from '@/hooks/useFeatureView';
import { getJobDrawerView } from '@/modules/jobs/views/jobDrawer.view';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons';
import { useUiFiltersStore } from '@/stores/useUiFiltersStore';
import { buildTodaysActiveJobsPath } from '@/modules/jobs/utils/jobFilters.utils';
import {
  SHIFT_CHECK_IN_STATUS_BADGE_CLASS,
  SHIFT_CHECK_IN_STATUS_LABEL,
  formatShiftCheckInTime,
  formatShiftJobTime,
  getShiftCheckInStatus,
  getShiftClientId,
  getShiftWorkerName,
  sortShiftsForToday,
} from '@/modules/dashboard/utils/todaysJobs.utils';

const PAGE_SIZE = 5;

interface TodaysJobsTableProps {
  todaysShifts: DashboardWorkerOnShiftResponse[];
  isLoading: boolean;
}

const TodaysJobsTable = ({ todaysShifts, isLoading }: TodaysJobsTableProps) => {
  const navigate = useNavigate();
  const setJobsDateRange = useUiFiltersStore((state) => state.setJobsDateRange);
  const { getClientById } = useClientStore();
  const { getJobById, fetchJobById } = useJobStore();
  const { getTerm } = useTemplateStore();
  const drawerView = useFeatureView(getJobDrawerView);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const sortedShifts = useMemo(
    () => sortShiftsForToday(todaysShifts),
    [todaysShifts],
  );

  const totalItems = sortedShifts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalItems);
  const paginatedShifts = sortedShifts.slice(startIndex, endIndex);

  // Keep the page in range when a refresh returns fewer shifts.
  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const handleJobClick = async (jobId: string) => {
    // The dashboard only preloads jobs starting within its own date range, so a
    // job that started earlier (e.g. an ongoing multi-day job) may not be in the
    // store yet, especially right after a page reload. Fetch it before opening
    // the drawer so JobDrawer never receives a null job.
    if (!getJobById(jobId)) {
      await fetchJobById(jobId);
    }
    setSelectedJobId(jobId);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedJobId(null);
  };

  const handleViewAll = () => {
    const today = dayjs();
    setJobsDateRange({
      startIso: today.startOf('day').toISOString(),
      endIso: today.endOf('day').toISOString(),
    });
    navigate(buildTodaysActiveJobsPath(today.format('YYYY-MM-DD')));
  };

  return (
    <div className="mb-4 flex flex-col rounded-xl bg-white p-4 shadow">
      <div className="mb-2 flex h-6 items-center justify-between">
        <div className="text-md font-semibold text-blue-900">
          Workers on job today <span className="text-sm">({totalItems})</span>
        </div>
        <button
          type="button"
          onClick={handleViewAll}
          className={`cursor-pointer text-sm font-medium ${actionLinkClassName}`}
        >
          View all
        </button>
      </div>
      {totalItems === 0 ? (
        <div className="flex flex-col items-center justify-center py-10">
          <div className="mb-6 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faCalendarDay}
              className="text-gray-400"
              style={{ fontSize: '3rem' }}
            />
          </div>
          <p className="mb-2 text-lg font-semibold text-gray-900">
            {isLoading ? 'Loading shifts...' : 'No jobs on today'}
          </p>
          <p className="max-w-sm text-center text-sm text-gray-500">
            {getTerm(TemplateTerm.WORKER, true)} rostered on today will appear
            here with their check-in status.
          </p>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-white">
                <tr className="border-t border-gray-200 text-xs text-gray-500">
                  <th className="py-2 text-left font-medium">
                    {getTerm(TemplateTerm.WORKER, false)}
                  </th>
                  <th className="py-2 text-left font-medium">Job title</th>
                  <th className="py-2 text-left font-medium">Job time</th>
                  <th className="py-2 text-left font-medium">Check-in time</th>
                  <th className="py-2 text-left font-medium">
                    {getTerm(TemplateTerm.CLIENT, false)}
                  </th>
                  <th className="py-2 text-left font-medium">
                    Timesheet status
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedShifts.map((shift) => {
                  const workerName = getShiftWorkerName(shift);
                  const clientId = getShiftClientId(shift);
                  const status = getShiftCheckInStatus(shift);
                  const jobId = shift.job?.id;
                  const canOpenJob = Boolean(jobId);
                  return (
                    <tr
                      key={`${shift.isoDate}-${jobId}-${shift.user?.id}`}
                      onClick={
                        canOpenJob && jobId
                          ? () => void handleJobClick(jobId)
                          : undefined
                      }
                      className={`border-t border-gray-100 ${
                        canOpenJob ? 'cursor-pointer hover:bg-gray-50' : ''
                      }`}
                    >
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          {shift.user?.profilePictureUrl ? (
                            <img
                              src={shift.user.profilePictureUrl}
                              alt={workerName}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-300 text-xs font-semibold text-gray-600">
                              {workerName
                                .split(' ')
                                .map((namePart) => namePart[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </span>
                          )}
                          <span
                            className="max-w-[180px] truncate text-xs font-semibold"
                            title={workerName}
                          >
                            {workerName || '-'}
                          </span>
                        </div>
                      </td>
                      <td
                        className="max-w-[200px] truncate py-2 text-xs"
                        title={shift.job?.title ?? undefined}
                      >
                        {shift.job?.title || '-'}
                      </td>
                      <td className="py-2 text-xs">
                        {formatShiftJobTime(shift)}
                      </td>
                      <td className="py-2 text-xs">
                        {formatShiftCheckInTime(shift)}
                      </td>
                      <td className="max-w-[180px] truncate py-2 text-xs">
                        {getClientById(clientId)?.name || clientId || '-'}
                      </td>
                      <td className="py-2">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${SHIFT_CHECK_IN_STATUS_BADGE_CLASS[status]}`}
                        >
                          {SHIFT_CHECK_IN_STATUS_LABEL[status]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-2 flex shrink-0 items-center justify-between border-t border-gray-100 pt-2">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {endIndex} of {totalItems} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <JobDrawer
        job={selectedJobId ? getJobById(selectedJobId) : null}
        isOpen={isDrawerOpen}
        view={drawerView}
        onClose={handleCloseDrawer}
      />
    </div>
  );
};

export default TodaysJobsTable;
